// Libraries
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, effect, ElementRef, EventEmitter, HostListener, inject, Input, NgZone, OnChanges, OnDestroy, OnInit, Renderer2, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addDays, addHours, addMinutes, addSeconds, differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds, endOfHour, format, formatISO, getUnixTime, parseISO, startOfDay, startOfHour, subHours } from 'date-fns';
import { jsPDF } from "jspdf";
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ReportStore } from 'src/app/services/stores/report.store';

// Interfaces
import { Attachment } from 'src/app/interfaces/api/attachment';
import { Event } from 'src/app/interfaces/api/event';
import { Metric } from 'src/app/interfaces/api/metric';
import { Project } from 'src/app/interfaces/api/project';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { Unit } from 'src/app/interfaces/api/unit';
import { User } from 'src/app/interfaces/api/user';
import { Config } from 'src/app/interfaces/shared/config';
import { Tag } from 'src/app/interfaces/shared/tag';
import { AvailableResource, InfoBox, Line, TimelineDataKey } from 'src/app/interfaces/shared/timeline';

// Classes
import { Pop } from 'src/app/classes/shared/pop';

// Tools
import { Tools } from 'src/app/classes/shared/tools';

// UI
import { ButtonComponent } from 'ui-library';

// Components
import { GalleryTool } from 'src/app/classes/gallery/gallery.tools';
import { ALL_ROLES, GalleryComponent, TagDisplayConfig } from 'src/app/components/shared/gallery/gallery.component';
import { ModuleType } from 'src/app/interfaces/shared/module';
import { MetricModifComponent } from './metric-modif/metric-modif.component';

interface ChangedTag {
  oldTag: string;
  newTag: string;
}

const INIT_SCALE: number = 5;

@Component({
  selector: 'app-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    TranslateModule,
    ButtonComponent
  ]
})

export class TimelineComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  // Services
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly tools: Tools = inject(Tools);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly metricService: MetricService = inject(MetricService);
  private readonly auth: AuthService = inject(AuthService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly pop: Pop = inject(Pop);
  private readonly galleryTool: GalleryTool = inject(GalleryTool)
  private readonly reportStore: ReportStore = inject(ReportStore);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly renderer: Renderer2 = inject(Renderer2);

  // Unique id for this timeline instance, used to keep a single hover popup open across all timelines
  private static popupCount = 0;
  private readonly popupId = `timeline-${TimelineComponent.popupCount++}`;

  // Guards against the scroll listener re-broadcasting a position we just applied ourselves
  private isApplyingExternalScroll: boolean = false;
  private clearExternalScrollFlagFrame: number | null = null;
  private pendingScrollFrame: number | null = null;
  private removeScrollListener: (() => void) | null = null;

  // Components API
  @ViewChild('timeline') timeline!: ElementRef;
  @ViewChild('scrollBar', { static: false }) scrollBarRef!: ElementRef;

  @Input() timelineData: Record<TimelineDataKey, any[]> = {
    attachments: [],
    events: [],
    metrics: []
  };

  @Input() startDate = formatISO(subHours(new Date(), 7));
  @Input() endDate = formatISO(new Date());
  @Input() eventTypes$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(["Generated event", "Flow event", "User defined", "Tracker log", "Risk"]);
  @Input() metricTypes$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  @Input() unit?: Unit;
  @Input() tracker?: Tracker;
  @Input() trackers: Tracker[] = [];
  @Input() availableResources: AvailableResource[] = [];
  @Input() singleTimeline: boolean = false;
  @Input() search: BehaviorSubject<string> = new BehaviorSubject<string>('');
  @Input() timelineUpdateSignal!: WritableSignal<any>;
  @Input() target: Signal<string> = signal('');

  // Signals
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;
  readonly currentUser: Signal<User> = this.auth.currentUser;

  // Variables
  loading: boolean = false;
  metrics: Metric[] = [];
  events: Event[] = [];
  attachments: Attachment[] = [];
  metricSub!: Subscription | null;
  attachmentSub: Subscription | null = null;
  eventSub!: Subscription | null;
  isClicked: boolean = false;

  timelineScrollLeft: number = 0;
  startDateTimeline!: Date;
  attachmentIndex: Record<string, any> = {};
  currentSearch: string = "";
  playerDuration: number = 0;
  ticks: { index: number, value: any }[] = [];
  lines: Line[] = [];
  attachmentLines: Line[] = [];
  eventLines: Line[] = [];

  lineHeight: number = 24;
  eventTop: number = this.lineHeight / 2;
  metricTop: number = this.lineHeight;
  hours: { label: string, position: number }[] = [];
  playerDate: Date = subHours(new Date(), 7);
  min: number = 0;
  max: number = differenceInSeconds(parseISO(this.endDate), parseISO(this.startDate));
  currentDate: string = format(this.playerDate, 'dd/MM/yyyy HH:mm:ss');
  hourSize: number = 100;//px
  graphWidth: number = 100;
  scale: number = INIT_SCALE;

  infoBox: InfoBox = {
    id: "",
    top: 0,
    left: 0,
    thumbnail: "",
    picture: "",
    data: [{
      key: "title",
      value: "title"
    }],
    compactData: {}
  };

  overflowX: string = "scroll";
  overflowY: string = "hidden";
  hoverInfo!: boolean;
  noDateSelected: boolean = true;
  attachment: Attachment | null = null;
  darkBarVisible: boolean = false;
  darkBarTop: number = 0;
  darkBarLeft: number = 0;
  darkBarHeight: number = 0;
  darkBarBottom: number = 0;
  minuteWidth: number = 8.3334;
  showRawData: boolean = false;
  trackersObj: Record<string, string> = {};
  keyPressed: string = "";
  partInt: any = null;
  partitionPlayer: any = null;
  destroy$ = new Subject<void>();
  tagNames: Record<ModuleType, string[]> = {
    llm: [],
    risk: [],
    crane: [],
    zone: [],
    action: [],
    worker: []
  };

  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      crane: { ai: ALL_ROLES, manual: ALL_ROLES },
      worker: { ai: ALL_ROLES, manual: ALL_ROLES },
      action: { ai: ALL_ROLES, manual: ALL_ROLES },
      zone: { ai: ALL_ROLES, manual: ALL_ROLES },
      risk: { ai: ['dev'], manual: ['dev', 'admin', 'userAdmin'] }
    }
  };

  filteredLines: Line[] = [];
  filteredEventLines: Line[] = [];
  filteredAttachmentLines: Line[] = [];
  highlightedMetricId: string = '';

  @HostListener('window:keydown', ['$event'])
  handleEnterboardEvent(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === 'Space') {
      if (this.partitionPlayer)
        this.partitionPlayer.stop();
    }
    if (this.keyPressed == "4" && event.key == "2") {
      this.playPartition();
      this.keyPressed = "";
    } else {
      this.keyPressed = event.key;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.hoverInfo) {
      this.infoBox.visible = false;
      this.reportStore.closePopup(this.popupId);
    }
  }

  constructor() {
    this.partitionPlayer = null;
    this.attachmentIndex = {};

    // Delete target mode
    effect(() => {
      const metricId = this.target();

      if (!metricId) {
        this.highlightedMetricId = '';
      } else if (this.lines.length > 0) {
        setTimeout(() => {
          this.focusOnMetric(metricId);
        }, 100);
      }
    });

    // Only one popup at a time: close ours as soon as another timeline opens its own.
    effect(() => {
      if (this.reportStore.activePopup() !== this.popupId && this.infoBox.visible) {
        this.infoBox.visible = false;
        this.darkBarVisible = false;
      }
    });
  }

  /////////////////////////////
  // Angular lifecycle hooks //
  /////////////////////////////

  async ngOnInit() {
    try {
      if (this.search && this.search.pipe) {
        this.search.pipe(takeUntil(this.destroy$)).subscribe(result => {
          if (result !== this.currentSearch) {
            this.currentSearch = result;
            this.filterLines();
          }
        });
      }

      this.metricSub = this.metricTypes$.pipe(takeUntil(this.destroy$)).subscribe(data => {
        this.ngOnChanges();
      });

      this.eventSub = this.eventTypes$.pipe(takeUntil(this.destroy$)).subscribe(data => {
        this.ngOnChanges();
      })

      this.attachmentIndex = {};

      this.galleryTool.getTagNames().then((tagNames) => {
        this.tagNames = tagNames;
      });

      // Scroll events don't affect any Angular-bound state here, only a raw DOM
      // property on other instances, so this stays outside the zone to avoid a
      // change-detection pass per scroll tick across every rendered timeline.
      this.ngZone.runOutsideAngular(() => {
        this.reportStore.timelineScrollChanges$.pipe(takeUntil(this.destroy$)).subscribe(({ sourceId, ratio }) => {
          if (sourceId === this.popupId || !this.scrollBarRef) return;

          this.isApplyingExternalScroll = true;
          this.scrollBarRef.nativeElement.scrollLeft = ratio * this.graphWidth;

          // Fallback in case the assignment above didn't actually move scrollLeft (e.g. this
          // instance was already at that position): no native 'scroll' event will fire then,
          // which would otherwise leave the flag stuck and swallow this instance's next
          // real user scroll tick.
          if (this.clearExternalScrollFlagFrame !== null)
            cancelAnimationFrame(this.clearExternalScrollFlagFrame);

          this.clearExternalScrollFlagFrame = requestAnimationFrame(() => {
            this.clearExternalScrollFlagFrame = null;
            this.isApplyingExternalScroll = false;
          });
        });
      });
    } catch (e) {
      console.error(e);
    }
  }

  ngOnChanges() {
    const scrollIndex = this.onScroll();
    this.parseData();
    this.updatePlayerTimeline();
    this.filterLines();

    if (this.scrollBarRef && !this.target) {
      this.scrollBarRef.nativeElement.scrollLeft = scrollIndex;
    }

    let startHour = parseISO(this.startDate).getHours();
    this.startDateTimeline = addHours(startOfDay(parseISO(this.startDate)), startHour);

    if (this.target && this.lines.length > 0) {
      setTimeout(() => {
        this.focusOnMetric(this.target());
      }, 100);
    }
  }

  ngAfterViewInit() {
    this.onZoom();
    this.getDatawithAttachment();

    if (this.scrollBarRef) {
      this.ngZone.runOutsideAngular(() => {
        this.removeScrollListener = this.renderer.listen(this.scrollBarRef.nativeElement, 'scroll', () => this.onBoardScroll());
      });
    }
  }

  ngOnDestroy(): void {
    if (this.metricSub)
      this.metricSub.unsubscribe();

    if (this.eventSub)
      this.eventSub.unsubscribe();

    this.reportStore.closePopup(this.popupId);

    if (this.removeScrollListener)
      this.removeScrollListener();

    if (this.pendingScrollFrame !== null)
      cancelAnimationFrame(this.pendingScrollFrame);

    if (this.clearExternalScrollFlagFrame !== null)
      cancelAnimationFrame(this.clearExternalScrollFlagFrame);

    this.destroy$.next();
    this.destroy$.complete();
  }

  // Broadcasts this timeline's scroll position to the other instances, as a ratio of its
  // own graph width so they stay aligned on the same point in time regardless of zoom scale.
  // Skips re-broadcasting a position that was just applied by another instance's scroll.
  private onBoardScroll(): void {
    if (this.isApplyingExternalScroll) {
      this.isApplyingExternalScroll = false;
      return;
    }

    if (this.pendingScrollFrame !== null) return;

    this.pendingScrollFrame = requestAnimationFrame(() => {
      this.pendingScrollFrame = null;

      if (!this.scrollBarRef || this.graphWidth <= 0) return;

      const ratio = this.scrollBarRef.nativeElement.scrollLeft / this.graphWidth;
      this.reportStore.broadcastTimelineScroll(this.popupId, ratio);
    });
  }

  onScroll(): number {
    if (this.scrollBarRef) {
      return this.scrollBarRef.nativeElement.scrollLeft;
    }
    return 0;
  }

  onMouseEnterInfo() {
    this.hoverInfo = true;
  }

  onMouseLeaveInfo() {
    this.hoverInfo = false;
  }

  onMouseEnterBar(event: MouseEvent, line: Line, posIndex: number) {
    if (!line.data) return;

    let data = line.data[posIndex];

    if (!data) return;

    this.infoBox.data = [];
    this.infoBox.compactData = {};
    this.infoBox.line = line;
    this.infoBox.posIndex = posIndex;

    Object.keys(data).forEach(key => {
      switch (key) {
        case 'id':
          this.infoBox.id = data[key];
          this.infoBox.compactData.id = data[key];
          break;
        case 'name':
          this.infoBox.name = this.getDisplayName(data[key]);
          this.infoBox.compactData.name = this.getDisplayName(data[key]);
          break;
        case 'userEmail':
          this.infoBox.userEmail = data[key];
          this.infoBox.compactData.userEmail = data[key];
          break;
        case 'tags':
          this.infoBox.tags = data[key];
          this.infoBox.compactData.tags = data[key];
          break;
        case 'customVisionIds':
          this.infoBox.customVisionIds = data[key];
          break;
        case 'thumbnail':
          this.infoBox.thumbnail = data[key];
          this.infoBox.compactData.thumbnail = data[key];
          break;
        case 'picture':
          this.infoBox.picture = data[key];
          this.infoBox.compactData.picture = data[key];
          break;
        case 'task':
          this.infoBox.planning = data.planning;
          this.infoBox.task = data[key];
          this.infoBox.compactData.planning = data.planning;
          this.infoBox.compactData.task = data[key];
          break;
        case 'startDate':
          let startDateString = format(new Date(data[key]), 'dd/MM/yyyy HH:mm:ss');
          this.infoBox.startDate = new Date(data[key]);
          this.infoBox.data.push({ key, value: startDateString });
          this.infoBox.compactData.startDate = this.infoBox.startDate;
          break;
        case 'endDate':
          let endDateString = format(new Date(data[key]), 'dd/MM/yyyy HH:mm:ss');
          this.infoBox.endDate = new Date(data[key]);
          this.infoBox.data.push({ key, value: endDateString });
          this.infoBox.compactData.endDate = this.infoBox.endDate;

          let durationMilliseconds = (this.infoBox.endDate?.getTime() || 0) - (this.infoBox.startDate?.getTime() || 0);

          let durationHours = Math.floor(durationMilliseconds / (1000 * 60 * 60));
          let durationMinutes = Math.floor((durationMilliseconds % (1000 * 60 * 60)) / (1000 * 60));
          let durationSeconds = Math.floor((durationMilliseconds % (1000 * 60)) / 1000);

          let hours = durationHours.toString().padStart(2, '0');
          let minutes = durationMinutes.toString().padStart(2, '0');
          let seconds = durationSeconds.toString().padStart(2, '0');

          let durationTime = `${hours}:${minutes}:${seconds}`;
          this.infoBox.compactData.durationTime = durationTime;
          break;
        case 'comment':
          this.infoBox.data.push({ key, value: data[key] });
          this.infoBox.compactData.comment = data[key];
          break;
        case 'shackleBattery':
          this.infoBox.data.push({ key, value: data[key] });
          this.infoBox.compactData.shackleBattery = data[key];
          break;
        case 'processed':
          this.infoBox.data.push({ key, value: data[key] });
          break;
        case 'customMetric':
          this.infoBox.compactData.customMetric = data[key];
          break;
        default:
          let value = data[key];

          if (value && value !== true && value !== false && typeof value !== "object" && key !== "color") {
            if (key.toLocaleLowerCase().includes("date")) value = format(new Date(value), 'dd/MM/yyyy HH:mm:ss');

            this.infoBox.data.push({ key, value })
            this.infoBox.compactData[key] = data[key];
          }
          break;
      }
    })

    if (this.singleTimeline) {
      const resourceIndex = this.availableResources.findIndex(r =>
        r.tracker == data.tracker ||
        r.trackers?.some(t => t.id == data.tracker) ||
        (data.unit && r.unit == data.unit)
      );

      if (resourceIndex >= 0) {
        this.infoBox.compactData = this.infoBox.compactData || {};
        const currentResource = this.availableResources[resourceIndex];

        this.infoBox.compactData.trackerName = currentResource?.name;

        const unitId = data.unit || currentResource?.unitObj?.id;
        const unitName =
          data.unitName ||
          currentResource?.unitObj?.name ||
          (currentResource?.type === "unit" ? currentResource?.name : undefined);

        if (unitName) {
          this.infoBox.compactData.unitName = unitName;
        }

        const unitTrackers = Array.from(new Set(
          this.availableResources.filter((r: any) =>
            (unitId && (r?.unitObj?.id === unitId || r?.unit === unitId)) ||
            (unitName && (r?.unitObj?.name === unitName || (r?.type === "unit" && r?.name === unitName))))
            .flatMap((r: any) => r?.tracker ? [r.tracker] : (r?.trackers?.map((t: any) => t.id) || []))
            .filter(Boolean)
        ));

        if (unitTrackers.length) {
          this.infoBox.compactData.trackers = unitTrackers;
        }

        if (currentResource?.type == "unit" && currentResource?.name) {
          this.infoBox.compactData.unitName = currentResource.name;
        }
      }
    } else {
      if (this.tracker?.name) {
        this.infoBox.compactData = this.infoBox.compactData || {};
        this.infoBox.compactData.trackerName = this.tracker.name;
      }

      if (this.unit?.name) {
        this.infoBox.compactData = this.infoBox.compactData || {};
        this.infoBox.compactData.unitName = this.unit.name;

        const unitTrackers = Array.from(new Set(
          (this.trackers || []).map((t) => t.id).filter(Boolean)
        ));

        if (unitTrackers.length) {
          this.infoBox.compactData.trackers = unitTrackers;
        }
      }
    }

    if (line?.name) {
      this.infoBox.compactData = this.infoBox.compactData || {};
      this.infoBox.compactData.metricName = line.name;
    }

    if (this.infoBox.data.length) {
      this.infoBox.visible = true;
      this.reportStore.openPopup(this.popupId);
    }

    if (event?.target) {
      const target = event.target as HTMLElement;
      const rect = target.getBoundingClientRect();
      this.infoBox.top = rect.bottom - 5;
      this.infoBox.left = rect.left + ((rect.right - rect.left) / 2) - 20;
    }

    const infoWidth = 220;
    const leftInfo = this.infoBox.left;
    const limiteBorder = leftInfo + infoWidth;
    const windowWidth = window.innerWidth;

    this.verifBorders(limiteBorder, leftInfo, windowWidth);
  }

  onMouseMove(event: MouseEvent) {
    if (!this.darkBarVisible) {
      this.darkBarVisible = true;
    }

    let timelineElement = document.querySelector('.timeline-container');

    if (!timelineElement) return;

    let timelineRect = timelineElement.getBoundingClientRect();
    let mouseX = event.clientX - timelineRect.left - 225;

    let scrollLeft = this.onScroll();
    let minuteWidth = this.minuteWidth / INIT_SCALE * this.scale;
    let positionInPixels = Math.round((mouseX + scrollLeft) / minuteWidth) * minuteWidth;
    this.darkBarLeft = positionInPixels;
    let positionInMinutes = positionInPixels / minuteWidth;

    if (this.infoBox.compactData && this.infoBox.compactData.metricName !== "Attachments") {
      let newStartDate = addMinutes(this.startDateTimeline, positionInMinutes);
      let newEndDate = addMinutes(newStartDate, 1);
      let closestAttachment = this.findClosestAttachment(newStartDate, newEndDate, this.infoBox.compactData.tracker);

      if (closestAttachment) {
        this.infoBox.compactData.thumbnail = closestAttachment.thumbnail || "";
        this.infoBox.thumbnail = closestAttachment.thumbnail || "";
        this.infoBox.picture = closestAttachment.picture || "";
        this.infoBox.tracker = typeof closestAttachment.tracker === 'string' ? closestAttachment.tracker : closestAttachment.tracker.id;
        this.infoBox.compactData.picture = closestAttachment.picture || "";
      } else {
        this.infoBox.compactData.thumbnail = "";
        this.infoBox.thumbnail = "";
        this.infoBox.picture = "";
      }
    }
  }

  findClosestAttachment(targetTimeStart: Date, targetTimeEnd: Date, tracker: string | Tracker) {
    let closestAttachment = null;
    let attachmentArray: (Attachment | undefined)[] = [];
    let middleDate = null;
    let start = this.getDateIndex(targetTimeStart)
    let end = this.getDateIndex(targetTimeEnd)
    middleDate = (targetTimeStart.getTime() + targetTimeEnd.getTime()) / 2;
    attachmentArray = attachmentArray.concat(this.attachmentIndex[start], this.attachmentIndex[end]);

    if (attachmentArray && attachmentArray.length > 0) {
      let minDifference = Infinity;

      for (const attachment of attachmentArray) {
        if (attachment && this.attachmentInScope(attachment, tracker)) {

          const attachmentDate = (new Date(attachment.startDate || '').getTime() + new Date(attachment.endDate || '').getTime()) / 2;
          const currentDifference = Math.abs(middleDate - attachmentDate);
          if (currentDifference < minDifference) {
            minDifference = currentDifference;
            closestAttachment = attachment;
          }
        }
      }
    }


    return closestAttachment;
  }

  attachmentInScope(attachment: any, tracker: string | Tracker): boolean {
    if (attachment.tracker == tracker) return true;

    if (this.trackers?.length || this.unit?.id) return this.matchesTrackers(attachment);

    return false;
  }


  verifBorders(infoLeft: number, bubbleLeft: number, windowWidth: number) {
    if (windowWidth - infoLeft < 0) {
      this.infoBox.left = this.infoBox.left - 180;
      document.querySelector('.info-box')?.classList.add('info-box-modified');
    } else {
      document.querySelector('.info-box')?.classList.remove('info-box-modified');
    }
  }

  onMouseLeaveBar() {
    // if (!this.hoverInfo)
    //   this.infoBox.visible = false;

    this.darkBarVisible = false;
  }

  getDatawithAttachment() {
    if (this.timelineData.attachments.length == 0)
      return;
    for (const attachment of this.timelineData.attachments[0].data) {
      let attachmentStartDate = new Date(attachment.startDate);
      let attachmentEndDate = new Date(attachment.endDate);

      const middleDate = (attachmentStartDate.getTime() + attachmentEndDate.getTime()) / 2;
      const roundedDateObject = new Date(middleDate);

      const index = this.getDateIndex(roundedDateObject);

      if (!this.attachmentIndex[index]) {
        this.attachmentIndex[index] = [];
      }

      this.attachmentIndex[index].push(attachment);
    }
  }

  getDateIndex(date: Date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hour = date.getHours().toString().padStart(2, '0');
    const minute = date.getMinutes().toString().padStart(2, '0');

    return `${year}${month}${day}${hour}${minute}`;
  }

  onReplayClick(route: string) {
    const project = this.currentProject();
    const encodeDate = (date: Date | string) => formatISO(date).replace(/\+/g, '%2B');

    let params: any = {
      mode: "replay",
      metricsOnly: false,
      play: true,
      startDate: this.infoBox.startDate ? encodeDate(addSeconds(this.infoBox.startDate, -10)) : "",
      endDate: this.infoBox.endDate ? encodeDate(this.infoBox.endDate) : ""
    };

    if (route.includes('dashboard')) {
      const dashboardId = project && typeof project !== 'string' && Array.isArray(project.dashboardOrder) ? project.dashboardOrder[0] : undefined;
      params.dashboardId = dashboardId;
    }

    if (this.infoBox.compactData.units)
      params["replayUnits"] = this.infoBox.compactData.units;
    else if (this.infoBox.compactData.unitName)
      params["replayUnits"] = this.infoBox.compactData.unitName;

    if (this.infoBox.planning) {
      params.planningName = this.infoBox.planning;
      params.taskName = encodeURIComponent(this.infoBox.task);
    }

    const queryString = Object.keys(params).map(key => `${key}=${params[key]}`).join('&');
    window.open(`${route}?${queryString}`, "_blank");
  }

  getTick(type: keyof typeof this.timelineData, name: string, startDate: string, endDate: string): any {
    let result = { index: -1, value: undefined };
    let data = this.timelineData[type];

    if (data) {
      let obj = data.find((d: any) => d.name && d.name.toLowerCase() == name.toLowerCase());

      if (obj) {
        let resIndex = obj.data.findIndex((d: any) => {
          const start = new Date(startDate);
          const dStart = new Date(d.startDate);
          const dEnd = new Date(d.endDate);
          return start >= dStart && start <= dEnd;
        });

        if (resIndex >= 0)
          result = { index: resIndex, value: obj.data[resIndex] };
      }
    }

    return result;
  }

  onLineClick(type: string, line: Line) {
    line.backgroundColor = "rgba(220,220,255, 0.5)"

    if (type == "metric") {
      this.lines.forEach((l: Line) => {
        if (l.name !== line.name) {
          l.backgroundColor = ""
        }
      })
    }

    if (type == "event") {
      this.eventLines.forEach((l: Line) => {
        if (l.name !== line.name) {
          l.backgroundColor = ""
        }
      })
    }

    if (type == "attachment") {
      this.attachmentLines.forEach(l => {
        if (l.name !== line.name) {
          l.backgroundColor = ""
        }
      })
    }

    if (!line.hasChildren) return;

    if (line.open)
      this.closeLine(type, line);
    else
      this.openLine(type, line);
  }

  openLine(type: string, line: Line) {
    if (type == "metric") {
      this.lines.forEach((l: Line) => {
        if (l.id === line.id) {
          l.open = true;
        } else if (l.parent == line.id || l.grandParent == line.id) {
          l.open = true;
          l.hidden = false;
        }
      })
    }

    if (type == "event") {
      this.eventLines.forEach((l: Line) => {
        if (l.id === line.id) {
          l.open = true;
        } else if (l.parent == line.id || l.grandParent == line.id) {
          l.open = true;
          l.hidden = false;
        }
      })
    }

    if (type == "attachment") {
      this.attachmentLines.forEach(l => {
        if (l.name === line.name) {
          l.open = true;
        } else if (l.parent === line.name) {
          l.hidden = false;
        }
      })
    }
  }

  closeLine(type: string, line: Line) {
    if (type == "metric") {
      this.lines.forEach((l: Line) => {
        if (l.id === line.id) {
          l.open = false;
        } else if (l.parent == line.id || l.grandParent == line.id) {
          l.open = false;
          l.hidden = true;
        }
      })
    }

    if (type == "event") {
      this.eventLines.forEach((l: Line) => {
        if (l.id === line.id) {
          l.open = false;
        } else if (l.parent === line.id || l.grandParent === line.id) {
          l.open = false;
          l.hidden = true;
        }
      })
    }

    if (type == "attachment") {
      this.attachmentLines.forEach(l => {
        if (l.name === line.name) {
          l.open = false;
        } else if (l.parent === line.name) {
          l.hidden = true;
        }
      })
    }
  }

  onImgClick() {
    //we should be able to open the gallery anyway
    const getImageInfo = (dir: any) => {
      let attach;
      let index = this.infoBox.posIndex

      if (dir == "next") {
        index = (index || 0) + 1;
      } else if (dir == "prev") {
        index = (index || 0) - 1;
      }

      if (dir == "") {
        attach = this.attachmentLines[0]?.data?.find((d: any) => d.picture == this.infoBox.picture);
        const attachIndex = this.attachmentLines[0]?.data?.findIndex((d: any) => d.picture == this.infoBox.picture) ?? -1;
        this.infoBox.posIndex = attachIndex;
      } else {
        this.infoBox.posIndex = index;
        attach = this.attachmentLines[0]?.data?.[index || 0];
      }

      let predictionName = this.translate.instant("dashboard.prediction");

      //TODO find a multilinguiste solution
      let craneCycleFr = "Coup de grue";
      let craneCycleEN = "Crane cycle";
      let concretingFr = "Coulage";
      let concretingEN = "Concreting";
      let startDate = attach.startDate;
      let endDate = attach.endDate;
      let prediction = this.getTick("metrics", predictionName, startDate, endDate);
      let craneCycle = this.getTick("metrics", craneCycleFr, startDate, endDate);
      let concreting = this.getTick("metrics", concretingFr, startDate, endDate);
      let risk = this.getTick("metrics", "Risk", startDate, endDate);

      if (craneCycle.index == -1)
        craneCycle = this.getTick("metrics", craneCycleEN, startDate, endDate);

      if (concreting.index == -1)
        concreting = this.getTick("metrics", concretingEN, startDate, endDate);

      if (this.attachmentLines[0]) {
        this.onMouseEnterBar(null as any, this.attachmentLines[0], this.infoBox.posIndex || 0);
      }

      let processed = attach.processed;
      // craneCycle = this.getTick("metrics", craneCycleName, startDate, endDate);
      let tracker = this.tools.isHex(this.infoBox.tracker || "") ? this.infoBox.tracker : this.trackersObj[this.infoBox.tracker || ""];
      let customVisionIds = attach.customVisionIds ?? {}

      let onUpdate = this.infoBox.onUpdate ?? new EventEmitter();

      onUpdate.subscribe((data: any) => {
        this.updateTimeline(data);
      })

      return {
        index: 0,
        id: this.infoBox.id,
        name: this.infoBox.name,
        tracker: tracker ?? this.infoBox.compactData.tracker,
        trackers: this.infoBox.compactData.trackers || [],
        urls: [this.infoBox.picture],
        tags: this.infoBox.tags,
        startDate,
        endDate,
        prediction: prediction.value,
        craneCycle: craneCycle.value,
        concreting: concreting.value,
        risk: risk.value,
        processed,
        user: this.currentUser(),
        project: this.currentProject(),
        customVisionIds,
        onUpdate,
        type: "Attachments",
        tagDisplayConfig: this.tagDisplayConfig,
        getImageInfo
      }
    }

    this.dialog.open(GalleryComponent, {
      width: "auto",
      height: "auto",
      data: getImageInfo("")
    });
  }

  private updateTimeline(res: any) {
    if (res && res.processed != undefined && this.attachmentLines[0]?.data && this.infoBox.posIndex !== undefined) {
      this.attachmentLines[0].data[this.infoBox.posIndex].processed = res.processed;

      if (res.url)
        this.attachmentLines[0].data[this.infoBox.posIndex].picture = res.url;
    }

    if (res && res.tags) {
      let { predictionNames, predictions } = this.parseTags(res.tags);
      this.infoBox.tags = predictions;

      if (this.attachmentLines[0]?.data && this.infoBox.posIndex !== undefined) {
        this.attachmentLines[0].data[this.infoBox.posIndex].tags = predictions;
      }

      if (res.changedTags.length != 0) {
        res.changedTags.forEach((changedTag: ChangedTag) => {
          this.attachmentLines[0]!.data.forEach((a: any) => {
            if (a.prediction == changedTag.oldTag && a) {
              a.crane = changedTag.newTag;

              a.tags?.crane?.forEach((b: Tag) => {
                if (!b.boundingBox) return;

                if (b.tagName && b.tagName == changedTag.oldTag) {
                  b.tagName = changedTag.newTag
                }
              })
            }
          });
        });

        this.pop.toast(`La fusion du tag "${res.changedTags[0].oldTag}" en "${res.changedTags[0].newTag}" s'est bien déroulé`, "success", 4000, "merge");
      }

      Object.keys(predictionNames).forEach(key => {
        let predictionName = key;

        if (!["action", "zone"].includes(predictionName))
          predictionName += "s";

        if (this.attachmentLines[0]?.data && this.infoBox.posIndex !== undefined) {
          this.attachmentLines[0].data[this.infoBox.posIndex][`${predictionName}`] = predictionNames[key]?.join(", ");
        }
      })

      if (this.attachmentLines[0]?.data && this.infoBox.posIndex !== undefined) {
        const updateData: any = {
          attachment: {
            ...this.attachmentLines[0].data[this.infoBox.posIndex],
            type: "Attachments"
          }
        };

        if (res.metric) {
          updateData.metric = res.metric;
        }

        if (res.event) {
          updateData.event = res.event;
        }

        if (this.timelineUpdateSignal) {
          this.timelineUpdateSignal.set(updateData);
        }
      }

      this.parseData();
      this.updatePlayerTimeline();
    }
  }

  // @HostListener("wheel", ["$event"])
  public onZoom(event: WheelEvent | null = null) {
    let board = document.getElementById('timeline-board')
    if (!board) return;
    let scrollLeft = board.scrollLeft;
    let boardWidth = board.clientWidth;
    let scale = this.scale;
    let prevScroll = board.scrollLeft;
    let ratio = prevScroll / this.graphWidth;
    let scrollPercent = 0;

    //find mouse pose in timeline
    if (event) {
      let relMousePos = scrollLeft + event.offsetX - (boardWidth / 2);
      scrollPercent = relMousePos / this.graphWidth;
      scale -= event.deltaY / 1000;
    }

    let start = this.startDate;
    let end = this.endDate;
    let graphWidth = this.getBarWitdh(formatISO(startOfHour(new Date(start))), formatISO(endOfHour(new Date(end)))) * scale;

    if (graphWidth > boardWidth) {
      this.scale = scale;
      this.graphWidth = graphWidth;
      board.scrollLeft = this.graphWidth * scrollPercent;
    }

    board.scrollLeft = this.graphWidth * ratio;
  }

  setZoom(zoom: number) {
    let board = document.getElementById('timeline-board')

    if (!board) return;

    let boardWidth = board.clientWidth;
    let scale = this.scale;
    let prevScroll = board.scrollLeft;
    let ratio = prevScroll / this.graphWidth;
    let scrollPercent = 0;

    //find mouse pose in timeline
    scale -= zoom;
    let start = this.startDate;
    let end = this.endDate;

    if (Math.abs(differenceInHours(parseISO(this.endDate), parseISO(this.startDate))) > 24)
      start = formatISO(subHours(new Date(end), 7));

    let graphWidth = this.getBarWitdh(formatISO(startOfHour(parseISO(this.startDate))), formatISO(endOfHour(parseISO(this.endDate)))) * scale;

    if (graphWidth > boardWidth) {
      this.scale = scale;
      this.graphWidth = graphWidth;
      board.scrollLeft = this.graphWidth * scrollPercent;
    }

    board.scrollLeft = this.graphWidth * ratio;
  }

  async exportTimeline() {
    this.overflowX = "visible";
    this.overflowY = "visible";

    await new Promise(resolve => setTimeout(resolve, 100))

    var doc = new jsPDF({
      unit: "px",
      hotfixes: ["px_scaling"],
      orientation: "landscape",
      format: [((this.attachmentLines.length + this.eventLines.length + this.lines.length) * this.lineHeight) + (this.lineHeight * 5), (this.graphWidth + 200)]
    });

    doc.html(this.timeline.nativeElement, {
      callback: (doc) => {
        doc.save(`timeline_unifield_${format(parseISO(this.startDate), 'dd-MM-yy')}.pdf`);
        this.overflowX = "scroll";
        this.overflowY = "hidden";
      },
      image: { type: 'jpeg', quality: 10 },
      html2canvas: { backgroundColor: '#FFFFFF' },
      x: 10,
      y: 10
    });
  }

  toggle(line: string) { }

  parseData() {
    let colors = Object.keys(Config.timelineColors).map(k => Config.timelineColors[k]);
    let latestDate: string | undefined;
    let firstDate: string | undefined;

    Object.keys(this.timelineData).forEach((key: string) => {
      (this.timelineData as any)[key].forEach((d: any, i: number) => {
        if (d.data.length == 0) return;

        if (!latestDate || (differenceInSeconds(new Date(latestDate), new Date(d.data[0].startDate)) < 0 && differenceInDays(new Date(latestDate), new Date(firstDate ?? this.startDate)) < 7))
          latestDate = d.data[0].startDate;

        if (!firstDate || differenceInSeconds(new Date(firstDate), new Date(d.data[d.data.length - 1].startDate)) > 0)
          firstDate = d.data[d.data.length - 1].startDate
      })
    })

    if (firstDate)
      this.startDate = formatISO(new Date(firstDate));

    if (latestDate && differenceInDays(new Date(latestDate), parseISO(this.startDate)) > 7)
      this.endDate = formatISO(addHours(addDays(parseISO(this.startDate), 7), 1));
    else if (latestDate)
      this.endDate = formatISO(addHours(new Date(latestDate), 1));


    Object.keys(this.timelineData).forEach((key: string) => {
      if (key == "attachments")
        this.attachmentLines = [];

      if (key == "events")
        this.eventLines = [];

      if (key == "metrics")
        this.lines = [];

      const data = (this.timelineData as any)[key];

      if (!["attachments", "events", "metrics"].includes(key) || !data || !Array.isArray(data)) return;

      data.sort((a: any, b: any) => {
        return a.name > b.name ? 1 : -1;
      })

      data.forEach((d: any, i: number) => {
        let cIndex = i;

        if (i >= colors.length) {
          cIndex = i % colors.length;
        }

        let color = d.color ? d.color : colors[cIndex];
        let data = [];
        let trackerIds = this.singleTimeline ? this.availableResources.filter(r => r.show).flatMap(r => [r.tracker, ...(r.trackers?.map(t => t.id) || [])]) : [];
        let trackerNames = this.singleTimeline ? this.availableResources.filter(r => r.show).flatMap(r => [r.name, ...(r.trackers?.map(t => t.name) || [])]) : [];
        trackerNames = this.singleTimeline ? trackerNames.concat(this.availableResources.filter(r => r.trackerObj).map(r => r.trackerObj?.name).filter(name => name !== undefined) as string[]) : trackerNames;
        this.trackersObj = {};

        trackerIds.forEach((id, index) => {
          const name = trackerNames[index];

          if (name && id)
            this.trackersObj[name] = id;
        });

        let unitNames = this.singleTimeline ? this.availableResources.filter(r => r.unitObj).map(r => r.unitObj?.name) : [];
        let unitIds = this.singleTimeline ? this.availableResources.map(r => r.unitObj?.id) : [];

        if (this.trackers?.length && !this.singleTimeline) {
          data = d.data.filter((item: any) => this.matchesTrackers(item))
        } else if (this.tracker && !this.singleTimeline) {
          data = d.data.filter((item: any) => item.tracker == this.tracker?.id || item.trackerName == this.tracker?.name || item.tracker == this.tracker?.name || (!item.tracker && !item.trackerName))
        } else {
          data = this.singleTimeline ? d.data.filter((item: any) => trackerIds.includes(item.tracker) || trackerNames.includes(item.trackerName) || trackerNames.includes(item.tracker) || unitNames.includes(item.unitName) || unitIds.includes(item.unit) || unitNames.includes(item.unit) || (!item.tracker && !item.trackerName)) : d.data;
        }

        let topLine: Line = {
          id: d.name,
          name: d.name,
          open: false,
          isParent: true,
          hasChildren: false,
          color,
          data,
          positions: data.map((item: any) => [this.getBarLeft(item.startDate), this.getBarWitdh(item.startDate, item.endDate)])
        }

        if (key == "attachments" && topLine.data.length) {
          this.attachmentLines.push(topLine)
        }

        let eventTypes = this.eventTypes$.value;

        if (key == "events" && eventTypes.indexOf(d.name) >= 0 && topLine.data.length)
          this.eventLines.push(topLine)

        const metricTypes = this.metricTypes$.value;

        if (key == "metrics" && metricTypes.indexOf(d.name) >= 0 && topLine.data.length)
          this.lines.push(topLine)

        if (((d.children.length && key == "events") || (d.children.length > 1 || (this.singleTimeline && d.children.length >= 0))) && topLine.data.length) {
          topLine.hasChildren = true;

          d.children.forEach((c: any, index: number) => {
            if (c.name == d.name) return;

            let data = [];

            if (this.trackers?.length && !this.singleTimeline) {
              data = c.data.filter((item: any) => this.matchesTrackers(item))
            } else if (this.tracker && !this.singleTimeline) {
              data = c.data.filter((item: any) => item.tracker == this.tracker?.id || item.trackerName == this.tracker?.name || item.tracker == this.tracker?.name || (!item.tracker && !item.trackerName))
            } else {
              data = this.singleTimeline ? c.data.filter((item: any) => trackerIds.includes(item.tracker) || trackerNames.includes(item.trackerName) || trackerNames.includes(item.tracker) || unitNames.includes(item.unitName) || unitIds.includes(item.unit) || unitNames.includes(item.unit) || (!item.tracker && !item.trackerName)) : c.data;
            }

            if (!data.length) return;

            let midLine: Line = {
              id: `${d.name}=>${c.name}`,
              name: c.name,
              parent: d.name,
              hidden: true,
              color,
              data,
              positions: data.map((item: any) => [this.getBarLeft(item.startDate), this.getBarWitdh(item.startDate, item.endDate)]),
              isParent: this.singleTimeline && key != "attachments",
              hasParent: true,
              hasChildren: false
            }

            if (key == "attachments")
              this.attachmentLines.push(midLine);

            if (key == "events" && eventTypes.indexOf(d.name) >= 0)
              this.eventLines.push(midLine)

            if (key == "metrics" && metricTypes.indexOf(d.name) >= 0)
              this.lines.push(midLine);

            if (this.singleTimeline && key != "attachments" && d.children[index].children.length && midLine.data.length && !d.children[index].noChild) {
              d.children[index].children.forEach((gc: any) => {
                if (d.name == gc.name) return;

                midLine.hasChildren = true;
                let data: any[] = gc.data.filter((item: any) => trackerIds.includes(item.tracker) || trackerNames.includes(item.trackerName) || trackerNames.includes(item.tracker) || unitNames.includes(item.unitName) || unitIds.includes(item.unit) || unitNames.includes(item.unit) || (!item.tracker && !item.trackerName));

                let bottomLine: Line = {
                  id: `${d.name}=>${c.name}=>${gc.name}`,
                  name: gc.name,
                  parent: midLine.id,
                  grandParent: d.name,
                  hidden: true,
                  color,
                  data,
                  positions: data.map((item: any) => [this.getBarLeft(item.startDate), this.getBarWitdh(item.startDate, item.endDate)]),
                  isParent: false,
                  hasChildren: false
                }

                if (key == "events" && eventTypes.indexOf(d.name) >= 0)
                  this.eventLines.push(bottomLine);

                if (key == "metrics" && metricTypes.indexOf(d.name) >= 0)
                  this.lines.push(bottomLine);
              })
            }
          })
        }
      })

      this.eventTop = (this.attachmentLines.length * this.lineHeight) + this.lineHeight / 2;
      this.metricTop = this.eventTop + (this.eventLines.length * this.lineHeight) + this.lineHeight / 2;
    })
  }

  // Whether a timeline item belongs to this unit timeline: it matches when the
  // item carries the unit's id/name, or belongs to any of the unit's trackers.
  // Tracker- and unit-less items (orphans) are kept, as in the per-tracker view.
  matchesTrackers(item: any): boolean {
    const ids = this.trackers.map((t) => t.id);
    const names = this.trackers.map((t) => t.name);

    const trackerMatch = ids.includes(item.tracker) || names.includes(item.trackerName) || names.includes(item.tracker);
    const unitMatch = !!this.unit?.id && (item.unit == this.unit.id || item.unitName == this.unit.name || item.unit == this.unit.name);
    const orphan = !item.tracker && !item.trackerName && !item.unit && !item.unitName;

    return trackerMatch || unitMatch || orphan;
  }

  getScaled(value: number): number {
    return value < 1 ? Math.ceil(value * this.scale) : value * this.scale;
  }

  getBarWitdh(startDate: string, endDate: string) {
    let diff = differenceInSeconds(new Date(startDate), new Date(endDate)) / 3600;
    return this.hourSize * Math.abs(diff);
  }

  getBarRight(startDate: string) {
    return this.getBarWitdh(startDate, this.endDate);
  }

  getBarLeft(startDate: string) {
    return this.getBarWitdh(formatISO(startOfHour(parseISO(this.startDate))), startDate);
  }

  /**
    * Set player duration
    *
    */
  setTimelineDuration() {
    var duration = differenceInSeconds(parseISO(this.endDate), parseISO(this.startDate));
    this.max = duration;
    this.playerDuration = duration;
    return this.playerDuration;
  };

  /**
   * update player timeline
   */
  updatePlayerTimeline() {
    let start = formatISO(startOfHour(parseISO(this.startDate)));
    let end = formatISO(endOfHour(parseISO(this.endDate)));
    this.graphWidth = this.getBarWitdh(start, end);
    let numberOfHours = differenceInHours(parseISO(end), parseISO(start));
    this.hours = [];

    for (let i = 0; i <= numberOfHours; i++) {
      this.hours.push({
        label: addHours(parseISO(this.startDate), i).getHours() + 'h',
        position: i * this.hourSize
      })
    }

  }

  onPickerChanged(result: { startDate?: Date, endDate?: Date }) {
    if (result.startDate && this.startDate != formatISO(result.startDate)) {
      this.startDate = formatISO(result.startDate);
    }

    if (result.endDate && this.endDate != formatISO(result.endDate)) {
      this.endDate = formatISO(result.endDate);
    }

    if (result.startDate || result.endDate)
      this.noDateSelected = false;
  }

  onDashboardClick() { }

  onMapsClick() { }

  onForgeClick() { }

  onRawDataClick() {
    this.showRawData = !this.showRawData;
  }

  /**
   * Just for fun partition player
   */
  playPartition() {
    if (this.partitionPlayer.playing) {
      clearInterval(this.partInt)
      this.partitionPlayer.stop();
      return;
    }

    let partition: any[] = [];
    let notesMap: Record<string, any[]> = {};

    let tpm = 0.1;//temps par minute (de timeline) en seconde
    let binning = 20;// groupement en nombre de seconde

    this.timelineData.attachments.forEach((m: any, i: number) => {
      m.data.forEach((d: any) => {
        let time = Math.floor(getUnixTime(new Date(d.startDate)) / binning) * binning;

        let obj = {
          date: d.startDate,
          name: d.name,
          note: 10,
          octave: 7,
          time: 0.05
        }

        const timeKey = time.toString();

        if (notesMap[timeKey]) {
          notesMap[timeKey].push(obj)
        } else {
          notesMap[timeKey] = [obj]
        }
      })
    })

    this.timelineData.metrics.forEach((m: any, i: number) => {
      let octave = this.timelineData.metrics.length - i;

      if (m.name == "Attentes") octave = 2;
      if (m.name == "Séquences") octave = 3;
      if (m.name == "Coup de grue") octave = 4;
      if (m.name == "Phase de grue") octave = 5;

      m.children.forEach((c: any, j: number) => {
        c.data.forEach((d: any) => {
          let time = Math.floor(getUnixTime(new Date(d.startDate)) / binning) * binning;

          let obj = {
            date: d.startDate,
            name: c.name,
            note: j,
            octave,
            time: Math.ceil(differenceInMinutes(new Date(d.endDate), new Date(d.startDate)))
          }

          const timeKey = time.toString();

          if (notesMap[timeKey]) {
            notesMap[timeKey].push(obj)
          } else {
            notesMap[timeKey] = [obj]
          }
        })
      })
    })

    Object.keys(notesMap).forEach((time: string) => {
      partition.push({ time, notes: notesMap[time] })
    })

    partition.sort((a, b) => a.time < b.time ? -1 : 1);
    this.partitionPlayer.partition = partition;
    this.partitionPlayer.play(tpm);
    let startTime = getUnixTime(startOfHour(parseISO(this.startDate)));
    let time = parseFloat(partition[0].time);
    this.darkBarVisible = true;

    this.partInt = setInterval(() => {
      this.darkBarLeft = Math.floor(((time - startTime) / 60) * this.minuteWidth);
      time++;
    }, Math.floor((1000 / 60) * tpm))
  }

  openModification(infoBox: InfoBox) {
    this.dialog.open(MetricModifComponent, {
      width: '500px',
      data: { infoBox }
    }).afterClosed().subscribe((res) => {
      if (res !== "false") {
        if (this.timelineUpdateSignal) {
          this.timelineUpdateSignal.set({ type: "metrics", payload: res });
        }
      }
    });
  }

  deleteMetric(infoBox: InfoBox) {
    this.pop.confirm(this.translate.instant('editor.confirm_delete_chart'), (confirm: any) => {
      if (confirm.ok) {
        this.metricService.removeMetric(infoBox.id).subscribe({
          next: res => {
            const infoBoxWithDeleteFlag = { ...infoBox.compactData, deleted: true, type: infoBox.compactData.metricName };

            if (this.timelineUpdateSignal) {
              this.timelineUpdateSignal.set({ type: "metrics", payload: infoBoxWithDeleteFlag });
            }

            this.pop.toast(this.translate.instant('events.metric_del'), 'success', 4000, 'delete');
          },
          error: error => {
            this.pop.error(this.translate.instant('events.metric_errored'));
          }
        });
      }
    });
  }

  getShackleColor(shackleBattery: number): string {
    return this.tools.getShackleColor(shackleBattery);
  }

  parseTags(tags: Record<string, any[]>): { predictionNames: Record<string, string[]>, predictions: Record<string, any[]> } {
    let predictionNames: Record<string, string[]> = {
      crane: [],
      risk: [],
      worker: [],
      concreting: [],
      action: [],
      zone: [],
    };

    let predictions: Record<string, any[]> = {
      crane: [],
      risk: [],
      worker: [],
      concreting: [],
      action: [],
      zone: [],
    }

    if (tags && Object.keys(tags).length)
      Object.keys(tags).forEach((key: string) => {
        let predictionArray = tags[key];
        let workers = 0;

        if (predictionArray) {
          predictionArray.forEach((p: any) => {
            if (predictions[key]) {
              predictions[key].push(p);
            }

            if (p.probability <= 0.5) return;

            if (p.name) {
              p.tagName = p.name;
              delete p.name;
            }

            if (key == "worker")
              workers++;

            if (predictionNames[key] && predictionNames[key].indexOf(p.tagName) == -1 && p.tagName != "Negative" && p.tagName) {
              predictionNames[key].push(p.tagName);
            }
          })
        }

        if (workers > 0 && predictionNames["worker"] && predictionNames["worker"].length)
          predictionNames["worker"] = [`${workers} ${predictionNames["worker"][0]}${workers > 1 ? "s" : ""}`];
      })

    return { predictionNames, predictions };
  }

  getBatteryColor(trackerBattery: number): string {
    return this.tools.getBatteryColor(trackerBattery);
  }

  filterLines() {
    const search = this.currentSearch.toLowerCase();

    if (!search) {
      // If search is empty, reset filters
      this.filteredLines = this.lines;
      this.filteredEventLines = this.eventLines;
      this.filteredAttachmentLines = this.attachmentLines;
      return;
    }

    // Filter metrics
    this.filteredLines = this.lines.map(line => ({
      ...line,
      data: line.data.filter((item: any, index: number) =>
        item.label?.toLowerCase().includes(search)
      ),
      positions: line.positions.filter((_, index: number) =>
        line.data[index]?.label?.toLowerCase().includes(search)
      )
    })).filter(line => line.data.length > 0);

    // Filter events
    this.filteredEventLines = this.eventLines.map(line => ({
      ...line,
      data: line.data.filter((item: any, index: number) =>
        item.label?.toLowerCase().includes(search)
      ),
      positions: line.positions.filter((_, index: number) =>
        line.data[index]?.label?.toLowerCase().includes(search)
      )
    })).filter(line => line.data.length > 0);

    // Filter attachments
    this.filteredAttachmentLines = this.attachmentLines.map(line => ({
      ...line,
      data: line.data.filter((item: any, index: number) =>
        item.label?.toLowerCase().includes(search) ||
        item.name?.toLowerCase().includes(search)
      ),
      positions: line.positions.filter((_, index: number) =>
        line.data[index]?.label?.toLowerCase().includes(search) ||
        line.data[index]?.name?.toLowerCase().includes(search)
      )
    })).filter(line => line.data.length > 0);
  }

  isLastVisibleLine(lines: Line[], currentIndex: number): boolean {
    for (let i = lines.length - 1; i >= 0; i--) {
      if (lines[i] && !lines[i]?.hidden) {
        return i === currentIndex;
      }
    }

    return false;
  }

  getDisplayName(title: string) {
    return this.galleryTool.displayNames()["crane"][title] || this.galleryTool.displayNames()["risk"][title] || title;
  }

  focusOnMetric(metricId: string) {
    if (!metricId) return;

    this.highlightedMetricId = metricId;

    setTimeout(() => {
      const graphElement = document.querySelector(`[data-metric-id="${metricId}"]`);

      if (graphElement) {
        (graphElement as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 100);
  }
}
